import { action, makeObservable, observable, runInAction } from "mobx";
import apiService from "./apiService";
import { wsService } from "./wsService";
import type { ChatTaskPlan } from "../models";

/** MobX store for the per-chat agent task plan (todo list) panel. */
class TaskPlanService {
  private _plans = observable.map<string, ChatTaskPlan>({}, { deep: false });
  private _closedChatIds = observable.set<string>();
  private _collapsedChatIds = observable.set<string>();

  constructor() {
    makeObservable(this);
    wsService.on('plan:updated', this._onPlanUpdated);
    wsService.on('plan:cleared', this._onPlanCleared);
  }

  public getPlan(chatId: string): ChatTaskPlan | undefined {
    return this._plans.get(chatId);
  }

  public isClosed(chatId: string): boolean {
    return this._closedChatIds.has(chatId);
  }

  public isCollapsed(chatId: string): boolean {
    return this._collapsedChatIds.has(chatId);
  }

  /** Fetches the plan for a chat from the server (e.g. on chat open/switch). */
  public async loadPlan(chatId: string): Promise<void> {
    const { plan } = await apiService.get<{ plan: ChatTaskPlan | null }>(`/api/plan/${chatId}`, { suppress404: true });
    runInAction(() => {
      if (plan) this._plans.set(chatId, plan);
      else this._plans.delete(chatId);
    });
  }

  @action
  public close(chatId: string): void {
    this._closedChatIds.add(chatId);
  }

  @action
  public toggleCollapsed(chatId: string): void {
    if (this._collapsedChatIds.has(chatId)) this._collapsedChatIds.delete(chatId);
    else this._collapsedChatIds.add(chatId);
  }

  /** Permanently deletes the plan from the server. */
  public async clearPlan(chatId: string): Promise<void> {
    await apiService.delete(`/api/plan/${chatId}`);
    runInAction(() => {
      this._plans.delete(chatId);
      this._closedChatIds.delete(chatId);
    });
  }

  private _onPlanUpdated = action((data: unknown) => {
    const d = data as { chatId: string; plan: ChatTaskPlan };
    this._plans.set(d.chatId, d.plan);
    // New agent progress should always surface, even if the user previously closed the panel
    this._closedChatIds.delete(d.chatId);
  });

  private _onPlanCleared = action((data: unknown) => {
    const d = data as { chatId: string };
    this._plans.delete(d.chatId);
    this._closedChatIds.delete(d.chatId);
  });
}

export const taskPlanService = new TaskPlanService();
export default taskPlanService;
